import LegalDocument, { type LegalSection } from "@/components/feature/LegalDocument";
import { SITE } from "@/config/site";

const sections: LegalSection[] = [
  {
    id: "scope",
    title: "1. About this policy",
    body: [
      `${SITE.name} ("we", "us", "our") operates a hyperlocal business directory for ${SITE.regions.join(", ")}. This Privacy Policy explains what personal information we collect, why we collect it, how we use and protect it, and the choices and rights available to you.`,
      "We process personal information in accordance with the Information Technology Act, 2000, the Information Technology (Reasonable Security Practices and Procedures and Sensitive Personal Data or Information) Rules, 2011, and the Digital Personal Data Protection Act, 2023 (DPDP Act). Where this policy refers to a Data Principal or Data Fiduciary, those terms carry the meaning given in the DPDP Act.",
      "By accessing or using the website, mobile experience and related services, you consent to the practices described in this policy. If you do not agree, please discontinue use of the service.",
    ],
  },
  {
    id: "collect",
    title: "2. Information we collect",
    body: [
      "Information you give us: name, email address, phone number, business details, address and locality, listing content, photographs and logos, reviews, and any message you send through contact or enquiry forms.",
      "Business and account information: for owners and paid plans, we collect business name, GSTIN (where provided), category, service areas, billing details and transaction references.",
      "Information collected automatically: IP address, device and browser type, operating system, pages viewed, referring URLs, approximate location derived from IP, and interactions such as clicks and searches.",
      "Cookies and similar technologies: small data files used to remember your preferences, keep you signed in, measure traffic and understand how the directory is used.",
    ],
  },
  {
    id: "purpose",
    title: "3. How we use your information",
    body: [
      "To provide, operate and improve the directory, including displaying listings, enabling search by category and location, and powering the owner dashboard.",
      "To publish and manage business listings, reviews and user-submitted content you choose to make public.",
      "To communicate with you — responding to enquiries, sending service notices, and, where you have opted in, sharing relevant updates about the directory.",
      "To process payments for paid plans, issue invoices and maintain financial records.",
      "To keep the platform secure, detect spam, fraud and abuse, enforce our Terms of Service, and comply with applicable law.",
      "To understand usage trends in an aggregated or de-identified form so we can improve the product.",
    ],
  },
  {
    id: "consent",
    title: "4. Lawful basis and consent",
    body: [
      "We process personal data on the basis of your consent, the performance of a contract (for example, providing a paid plan you purchase), compliance with a legal obligation, and legitimate uses permitted under the DPDP Act.",
      "Where processing is based on consent, you may withdraw it at any time by writing to our Grievance Officer. Withdrawal does not affect the lawfulness of processing carried out before the withdrawal, and may limit your ability to use certain features.",
      "We do not use personal data for automated decision-making that produces legal or similarly significant effects without human review.",
    ],
  },
  {
    id: "cookies",
    title: "5. Cookies and tracking",
    body: [
      "Essential cookies are required for the website to function, such as keeping you logged in and remembering preferences. Without these, parts of the service may not work.",
      "Analytics cookies help us measure traffic and understand how visitors use the directory. You can control or delete cookies through your browser settings; disabling certain cookies may affect functionality.",
    ],
  },
  {
    id: "sharing",
    title: "6. Sharing and disclosure",
    body: [
      "Business listing information that owners choose to publish (name, address, contact details, hours, media, reviews) is displayed publicly as part of the directory.",
      "Service providers: we share data with trusted vendors who help us run the platform, such as hosting, storage, analytics and payment processing. They are bound to use the data only for the services they provide to us.",
      "Legal requirements: we may disclose information where required by law, court order, or a lawful request from a competent authority, or to protect the rights, safety and security of our users, the public or ourselves.",
      "We do not sell your personal information to third parties. Advertising on the platform is not used to sell personal data of visitors.",
    ],
  },
  {
    id: "retention",
    title: "7. Data retention and security",
    body: [
      "We retain personal data only for as long as necessary for the purposes described in this policy, or as required by law. Where data is no longer needed, we delete or anonymise it.",
      "We apply reasonable security practices and procedures — including access controls, encryption in transit and monitoring — to protect data against unauthorised access, alteration, disclosure or destruction. No method of transmission or storage is completely secure, and we cannot guarantee absolute security.",
      "In the unlikely event of a personal data breach, we will notify affected Data Principals and the Data Protection Board of India as required under the DPDP Act.",
    ],
  },
  {
    id: "rights",
    title: "8. Your rights",
    body: [
      "Under the Digital Personal Data Protection Act, 2023, you have the right to access the personal data we hold about you, to request correction, completion or updating of inaccurate data, and to request erasure when the data is no longer needed for the purpose for which it was collected.",
      "You also have the right to nominate another individual to exercise your rights in the event of death or incapacity, and the right to grievance redressal.",
      "To exercise any of these rights, contact our Grievance Officer using the details below. We may need to verify your identity before acting on a request.",
    ],
  },
  {
    id: "children",
    title: "9. Children's privacy",
    body: [
      "The directory is intended for users aged 18 and above. We do not knowingly collect personal data from children. If we learn that we have collected such data, we will take steps to delete it. If you believe a child has provided us with personal data, please contact us.",
    ],
  },
  {
    id: "updates",
    title: "10. Changes to this policy",
    body: [
      "We may update this policy from time to time to reflect changes in our practices or the law. Material changes will be posted on this page with a revised “Last updated” date. Your continued use of the service after changes take effect constitutes acceptance of the revised policy.",
    ],
  },
  {
    id: "law",
    title: "11. Governing law and jurisdiction",
    body: [
      "This policy is governed by the laws of India. Any dispute arising out of or in connection with this policy is subject to the exclusive jurisdiction of the courts at Mysore, Karnataka, India.",
    ],
  },
];

export default function PrivacyPolicy() {
  return (
    <LegalDocument
      crumb="Privacy Policy"
      eyebrow="Legal"
      icon="ri-shield-user-line"
      heading="Privacy Policy"
      subtitle={`How ${SITE.name} collects, uses, stores and protects your personal information, and the rights you hold under Indian data protection law.`}
      updated="October 2026"
      seoTitle="Privacy Policy"
      seoDescription={`Learn how ${SITE.name} collects, uses and protects your personal data under the IT Act, 2000 and the Digital Personal Data Protection Act, 2023.`}
      seoKeywords="Mysore Yellow Pages privacy policy, DPDP Act data protection, directory privacy Karnataka"
      path="/privacy-policy"
      sections={sections}
    />
  );
}